import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { APP_ROUTES } from '@/navigation';
import { useStore } from '@/state/store';
import { TuneDocumentActions } from './TuneDocumentActions';
import { AddTuneToPlaylist } from './AddTuneToPlaylist';
import { SetStrip, type SetStripControls } from './SetStrip';
import { useOwnHeight } from './useOwnHeight';
import {
  lastTuneWorkspace,
  rememberTuneWorkspace,
  tuneWorkspaceKey,
  type TuneWorkspaceMode,
} from './tuneWorkspace';
import './tuneWorkspace.css';

const MODES: {
  id: TuneWorkspaceMode;
  label: string;
  /** Words the narrowest phones drop from sight (screen readers keep them). */
  more?: string;
  description: string;
}[] = [
  { id: 'practise', label: 'Practise', description: 'Read the tune and play with the band' },
  { id: 'arrange', label: 'Arrange', description: 'Explore comping and chord-melody voicings' },
  {
    id: 'edit',
    label: 'Edit',
    more: ' score',
    description: 'Edit the written notes, chords and score',
  },
];

export function TuneWorkspaceHeader({
  mode,
  details,
  fileActions,
  playbackStatus,
  formCycling = false,
  children,
  onModeChange,
}: {
  mode: TuneWorkspaceMode;
  /** Additional Workbench metadata fields, after the shared title/composer. */
  details?: ReactNode;
  /** Route-specific New, Import and Export actions in the phone's Tune menu. */
  fileActions?: (close: () => void) => ReactNode;
  playbackStatus?: string;
  /** The band is cycling the form: set auto-advance holds on this tune. */
  formCycling?: boolean;
  children?: ReactNode;
  /** Clear page-local editing UI when changing workspace purpose. */
  onModeChange?: (mode: TuneWorkspaceMode) => void;
}) {
  const scoreId = useStore((s) => tuneWorkspaceKey(s.score));
  const lens = useStore((s) => s.voicingLens);
  const setLens = useStore((s) => s.setVoicingLens);
  const inRouter = useInRouterContext();
  const measureHeader = useOwnHeight('--collapse-height', '--workspace-header-height');

  useEffect(() => {
    rememberTuneWorkspace(scoreId, mode, lens);
  }, [scoreId, mode, lens]);

  const select = (next: TuneWorkspaceMode) => {
    let nextLens = lens;
    if (next === 'practise') nextLens = 'none';
    if (next === 'arrange' && lens === 'none') {
      nextLens = lastTuneWorkspace(scoreId)?.arrangeLens ?? 'comping';
    }
    try {
      if (nextLens !== lens) setLens(nextLens);
    } catch {
      // The document header separately reports unavailable local storage.
      // A view switch must remain navigable when persistence is unavailable.
    }
    rememberTuneWorkspace(scoreId, next, nextLens);
    onModeChange?.(next);
  };

  const documentActions = (setControls: SetStripControls | null) => (
    <AddTuneToPlaylist>
      {(playlistControls) => (
        <TuneDocumentActions
          details={details}
          fileActions={fileActions}
          playbackStatus={playbackStatus}
          playlistControls={playlistControls}
          setControls={setControls}
        />
      )}
    </AddTuneToPlaylist>
  );

  return (
    <header
      ref={measureHeader}
      className="tune-workspace-header no-print"
      data-testid="tune-workspace-header"
    >
      {/* Keep set subscriptions alive even while every menu is closed.
          Studio does not consume requests to advance band playback. */}
      {mode === 'edit' ? (
        documentActions(null)
      ) : (
        <SetStrip formCycling={formCycling}>{documentActions}</SetStrip>
      )}
      <div className="tune-workspace-navigation">
        <nav className="tune-workspace-modes" aria-label="Tune workspace">
          {MODES.map((item) => {
            const to = item.id === 'edit' ? APP_ROUTES.scoreStudio : APP_ROUTES.workbench;
            const content = (
              <>
                {item.label}
                {item.more && <span className="tune-workspace-mode-more">{item.more}</span>}
              </>
            );
            const props = {
              className: `tune-workspace-mode${mode === item.id ? ' is-active' : ''}`,
              'aria-current': mode === item.id ? ('page' as const) : undefined,
              'data-testid': `workspace-${item.id}`,
              title: item.description,
              onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                if (
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                )
                  return;
                // Practise and Arrange share a page. Keep its query while a
                // linked tune is still loading instead of cancelling that load.
                if ((mode === 'edit') === (item.id === 'edit')) event.preventDefault();
                select(item.id);
              },
            };
            return inRouter ? (
              <Link key={item.id} to={to} {...props}>
                {content}
              </Link>
            ) : (
              <a key={item.id} href={`${import.meta.env.BASE_URL}${to.slice(1)}`} {...props}>
                {content}
              </a>
            );
          })}
        </nav>
        {children && <div className="tune-workspace-extra">{children}</div>}
      </div>
    </header>
  );
}
